import { useEffect, useState } from 'react';

const cn = (...classes) => classes.filter(Boolean).join(' ');

const typographyVariants = {
    h4: 'text-[2.125rem] leading-[1.235] font-normal',
    body1: 'text-base leading-6 font-normal',
    body2: 'text-sm leading-5 font-normal',
    caption: 'text-xs leading-4 font-normal',
};

const resolveSx = (sx) => {
    if (!sx) {
        return undefined;
    }

    if (typeof sx === 'function') {
        return sx({
            palette: {
                background: { paper: '#171717' },
                divider: '#3f3f46',
            },
        });
    }

    return sx;
};

export const Box = ({ component = 'div', className, children, sx, ...props }) => {
    const Component = component;
    const style = resolveSx(sx);
    return (
        <Component className={className} style={style} {...props}>
            {children}
        </Component>
    );
};

export const Typography = ({ variant = 'body1', className, children, color, fontWeight, ...props }) => {
    const Tag = variant === 'h4' ? 'h4' : 'p';
    const colorClass = color === 'text.secondary' ? 'text-zinc-400' : 'text-zinc-100';
    const weightClass = fontWeight ? '' : '';

    return (
        <Tag className={cn(typographyVariants[variant], colorClass, weightClass, className)} style={fontWeight ? { fontWeight } : undefined} {...props}>
            {children}
        </Tag>
    );
};

export const FormControl = ({ className, children, fullWidth, variant, ...props }) => (
    <div className={cn('relative', fullWidth && 'w-full', className)} {...props}>
        {children}
    </div>
);

const InputLabel = ({ className, children, ...props }) => (
    <label className={cn('pointer-events-none absolute left-3 top-0 z-10 -translate-y-1/2 bg-zinc-900 px-1 text-sm font-semibold text-zinc-300', className)} {...props}>
        {children}
    </label>
);

export const InputAdornment = ({ className, children, position }) => <span className={cn(position === 'start' ? 'mr-2' : 'ml-2', 'inline-flex items-center', className)}>{children}</span>;

export const IconButton = ({ className, children, type = 'button', edge, ...props }) => (
    <button
        type={type}
        className={cn('inline-flex h-8 w-8 items-center justify-center border-0 bg-transparent p-0 text-[22px] text-zinc-200 shadow-none outline-none transition hover:bg-transparent hover:text-zinc-100', className)}
        {...props}
    >
        {children}
    </button>
);

export const CircularProgress = ({ size = 16, className }) => (
    <span
        className={cn('inline-block animate-spin rounded-full border-2 border-current border-r-transparent border-t-transparent', className)}
        style={{ width: size, height: size }}
        aria-label="loading"
    />
);

export const OutlinedInput = ({
    className,
    fullWidth,
    type = 'text',
    value,
    onChange,
    placeholder,
    endAdornment,
    error,
    label,
    floatingLabel = false,
    showPlaceholderOnFocus = false,
    ...props
}) => {
    const [isFocused, setIsFocused] = useState(false);
    const hasValue = value !== undefined && value !== null && String(value).length > 0;
    const isFloating = floatingLabel && Boolean(label);
    const isShrunk = isFloating && (isFocused || hasValue);

    const effectivePlaceholder = showPlaceholderOnFocus && !isFocused && !hasValue ? '' : placeholder;

    return (
        <div
            className={cn(
                'relative flex h-12 items-center rounded-[10px] border bg-zinc-800 px-3 text-zinc-100 transition-colors',
                error ? 'border-red-500 hover:border-red-500' : isFocused ? 'border-sky-500 hover:border-sky-500' : 'border-zinc-600 hover:border-zinc-400',
                fullWidth && 'w-full',
                className,
            )}
        >
            {isFloating ? (
                <label
                    className={cn(
                        'pointer-events-none absolute left-3 z-10 bg-zinc-800 px-1 text-zinc-300 transition-all duration-150',
                        isShrunk ? '-top-[1px] -translate-y-1/2 text-xs font-semibold text-sky-400' : 'top-1/2 -translate-y-1/2 text-base font-medium',
                    )}
                >
                    {label}
                </label>
            ) : null}

            <input
                type={type}
                value={value}
                onChange={onChange}
                onFocus={(event) => {
                    setIsFocused(true);
                    props.onFocus?.(event);
                }}
                onBlur={(event) => {
                    setIsFocused(false);
                    props.onBlur?.(event);
                }}
                placeholder={effectivePlaceholder}
                className={cn(
                    'h-full w-full border-0 bg-transparent text-base leading-none text-zinc-100 outline-none placeholder:text-zinc-500',
                    isFloating && 'pt-2',
                )}
                {...props}
            />
            {endAdornment}
        </div>
    );
};

export const TextField = ({ className, margin, label, helperText, error, fullWidth, ...props }) => (
    <div className={cn(margin === 'dense' ? 'mt-2' : '', fullWidth && 'w-full', className)}>
        {label ? <InputLabel>{label}</InputLabel> : null}
        <OutlinedInput className={label ? 'mt-3' : ''} error={error} fullWidth={fullWidth} {...props} />
        {helperText ? <p className={cn('mt-1 text-xs', error ? 'text-red-400' : 'text-zinc-400')}>{helperText}</p> : null}
    </div>
);

export const Button = ({ className, variant = 'contained', fullWidth, loading, disabled, children, type = 'button', disableElevation, size, ...props }) => {
    const isDisabled = disabled || loading;

    return (
        <button
            type={type}
            disabled={isDisabled}
            className={cn(
                'inline-flex h-12 cursor-pointer items-center justify-center rounded-[10px] px-4 text-base font-medium transition disabled:cursor-not-allowed',
                variant === 'contained'
                    ? 'bg-sky-500 text-white hover:bg-sky-500 disabled:bg-sky-700/50 disabled:text-zinc-300'
                    : 'border border-sky-500 bg-transparent text-sky-500 hover:bg-sky-500/10 disabled:border-zinc-600 disabled:text-zinc-500',
                fullWidth && 'w-full',
                className,
            )}
            {...props}
        >
            {loading ? (
                <span className="inline-flex items-center gap-2">
                    <CircularProgress size={18} className="text-zinc-300" />
                    <span>{children}</span>
                </span>
            ) : (
                children
            )}
        </button>
    );
};

export const Checkbox = ({ className, size, ...props }) => (
    <input
        type="checkbox"
        className={cn(
            "tk-checkbox mt-[2px] h-4 w-4 cursor-pointer appearance-none rounded-[2px] border border-sky-500 bg-transparent bg-center bg-no-repeat outline-none transition",
            "hover:bg-transparent focus:outline-none focus:ring-0",
            "checked:border-sky-500 checked:bg-sky-500",
            className,
        )}
        {...props}
    />
);

export const FormControlLabel = ({ className, control, label, ...props }) => (
    <label className={cn('inline-flex cursor-pointer items-center gap-2 text-zinc-200', className)} {...props}>
        {control}
        {label}
    </label>
);

export const Link = ({ className, children, variant, size, sx, ...props }) => (
    <a className={cn('cursor-pointer text-sky-500 transition hover:text-sky-400', className)} {...props}>
        {children}
    </a>
);

export const Chip = ({ className, color = 'default', component: Component = 'span', label, size, sx, ...props }) => (
    <Component
        className={cn(
            'inline-block h-3 w-3 rounded-full',
            color === 'success' ? 'bg-green-500' : color === 'error' ? 'bg-red-500' : 'bg-zinc-500',
            className,
        )}
        style={{
            backgroundColor: color === 'success' ? '#4caf50' : color === 'error' ? '#f44336' : '#71717a',
        }}
        {...props}
    />
);

export const Dialog = ({ open, onClose, children, maxWidth = 'xs', fullWidth = false }) => {
    useEffect(() => {
        if (!open) {
            return undefined;
        }

        const handleEscape = (event) => {
            if (event.key === 'Escape') {
                onClose?.();
            }
        };

        window.addEventListener('keydown', handleEscape);
        return () => {
            window.removeEventListener('keydown', handleEscape);
        };
    }, [open, onClose]);

    if (!open) {
        return null;
    }

    const widthClass = maxWidth === 'xs' ? 'max-w-sm' : 'max-w-lg';

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4" role="dialog" aria-modal="true">
            <div className="absolute inset-0" onClick={onClose} aria-hidden="true" />
            <div className={cn('relative w-full rounded-xl border border-zinc-700 bg-zinc-900 p-4 shadow-2xl', fullWidth && 'w-full', widthClass)}>{children}</div>
        </div>
    );
};

export const DialogTitle = ({ className, children, ...props }) => (
    <h2 className={cn('mb-2 text-lg font-semibold text-zinc-100', className)} {...props}>
        {children}
    </h2>
);

export const DialogContent = ({ className, children, ...props }) => (
    <div className={cn('py-1', className)} {...props}>
        {children}
    </div>
);

export const DialogActions = ({ className, children, ...props }) => (
    <div className={cn('mt-3 flex items-center justify-end gap-2', className)} {...props}>
        {children}
    </div>
);

